"use client";

import * as React from "react";
import { Download, X } from "lucide-react";
import { BottomSheet } from "./bottom-sheet";
import { cn } from "../../lib/utils";

export interface PayoutHistoryItem {
  id: string;
  date: string;
  month: string;
  amount: string;
  clicks: number;
  applications: number;
  referralBonus?: string;
  receiptUrl?: string;
}

export interface PayoutHistoryBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  payouts: PayoutHistoryItem[];
  title?: string;
  subtitle?: string;
  isLoading?: boolean;
}

export function PayoutHistoryBottomSheet({
  isOpen,
  onClose,
  payouts,
  title = "Payouts",
  subtitle = "View the details of payouts received for Ad network.",
  isLoading = false,
}: PayoutHistoryBottomSheetProps) {
  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      className="bg-[#F9FAFB] px-0 pb-6 pt-2 h-[70vh] max-h-[90vh]"
      showDragHandle
    >
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="px-4 pb-4 space-y-1">
          <p className="font-plus-jakarta-sans text-lg font-bold text-[#101828]">{title}</p>
          <p className="text-sm text-[#667085]">{subtitle}</p>
        </div>

        {/* Content */}
        <div className={cn(
          "flex-1 px-4 space-y-4",
          payouts.length > 0 ? "overflow-y-auto" : "overflow-hidden"
        )}>
          {isLoading ? (
            <div className="flex items-center justify-center py-10">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : payouts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center space-y-2">
              <p className="text-sm font-medium text-[#101828]">No payouts yet</p>
              <p className="text-xs text-[#667085]">When you receive a payout, it will appear here.</p>
            </div>
          ) : (
            payouts.map((payout) => (
              <PayoutCard key={payout.id} payout={payout} />
            ))
          )}
        </div>
      </div>
    </BottomSheet>
  );
}

function PayoutCard({ payout }: { payout: PayoutHistoryItem }) {
  const handleDownload = () => {
    if (payout.receiptUrl) {
      window.open(payout.receiptUrl, "_blank");
    }
  };

  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-[#F2F4F7] bg-white p-4 shadow-sm">
      {/* Top Row: Date and Download */}
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium text-[#98A2B3]">{payout.date}</p>
        <div className="flex h-8 w-8 items-center justify-center rounded-full invisible">
          <Download className="size-4 text-[#667085]" />
        </div>
      </div>

      {/* Middle Row: Month */}
      <div>
        <p className="font-plus-jakarta-sans text-xl font-bold text-[#344054]">{payout.month}</p>
      </div>

      {/* Stats Row: Badges (Hidden as requested) */}
      {/* 
      <div className="flex flex-wrap gap-2">
        <div className="rounded-md border border-[#F2F4F7] bg-[#F9FAFB] px-2.5 py-1">
          <p className="text-[10px] font-semibold text-[#667085]">
            Valid Clicks: <span className="text-[#101828]">{payout.clicks}</span>
          </p>
        </div>
        <div className="rounded-md border border-[#F2F4F7] bg-[#F9FAFB] px-2.5 py-1">
          <p className="text-[10px] font-semibold text-[#667085]">
            Valid Applications: <span className="text-[#101828]">{payout.applications}</span>
          </p>
        </div>
      </div>
      */}

      {/* Bottom Row: Bonus and Amount */}
      <div className="mt-2 flex items-end justify-between">
        <div>
          {payout.referralBonus && (
            <p className="text-xs font-bold text-[#ED5AA9]">
              {payout.referralBonus} Referral bonus
            </p>
          )}
        </div>
        <div>
          <p className="font-plus-jakarta-sans text-3xl font-bold text-[#101828]">{payout.amount}</p>
        </div>
      </div>
    </div>
  );
}
